import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";

import {
  AdLandingHero,
  QuickQuoteForm,
  ReviewCards,
  SettlementSteps,
  TrustAndOffices,
  WhatsIncluded,
} from "@/components/ad-landing";
import { FaqList, PeopleStrip } from "@/components/page-parts";
import {
  FEES,
  FIRM,
  PEOPLE,
  faqSchema,
} from "@/lib/firm";
import {
  CONVEYANCING_LOCATIONS,
  getConveyancingLocation,
  officeForSlug,
  type ConveyancingLocation,
} from "@/lib/conveyancing-locations";

import { LocalConveyancingDepth } from "@/components/local-conveyancing-depth";
import { CALOUNDRA_DEPTH } from "@/lib/local-conveyancing-depth";

const SITE = "https://coastsidelaw.com.au";

export const Route = createFileRoute("/conveyancing/$suburb")({
  loader: ({ params }) => {
    const content = getConveyancingLocation(params.suburb);
    if (!content) throw redirect({ href: "/conveyancing", statusCode: 301 });
    return { content };
  },
  head: ({ params, loaderData }) => {
    const content = loaderData?.content;
    if (!content) {
      return { meta: [{ title: fitTitle("Not found") }, { name: "robots", content: "noindex" }] };
    }
    const url = `${SITE}/conveyancing/${params.suburb}`;
    const office = officeForSlug(params.suburb);
    const person = content.personSlug
      ? PEOPLE.find((pp) => pp.slug === content.personSlug)
      : undefined;
    const personImage = person ? `${SITE}${person.photo}` : undefined;
    return {
      meta: [
        { title: fitTitle(content.metaTitle) },
        { name: "description", content: fitDescription(content.metaDescription ?? content.description) },
        { property: "og:title", content: fitTitle(content.metaTitle) },
        { property: "og:description", content: fitDescription(content.metaDescription ?? content.description) },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(personImage
          ? [
              { property: "og:image", content: personImage },
              { name: "twitter:image", content: personImage },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqSchema(params.suburb === "caloundra" ? [...content.faqs, ...CALOUNDRA_DEPTH.extraFaqs] : content.faqs)) },
        ...(person && personImage
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Person",
                  name: person.name,
                  jobTitle: person.role,
                  image: {
                    "@type": "ImageObject",
                    url: personImage,
                    caption: `${person.name}, ${person.role} at ${FIRM.name}, conveyancing in ${content.name}`,
                  },
                  url,
                  telephone: FIRM.phone,
                  knowsAbout: [...person.areas],
                  workLocation: {
                    "@type": "Place",
                    name: `${content.name}, Queensland`,
                  },
                  worksFor: { "@id": "https://coastsidelaw.com.au/#legalservice" },
                }),
              },
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LegalService",
            "@id": `${url}#legalservice`,
            name: `${FIRM.name} — conveyancing solicitors, ${content.name}`,
            url,
            description: content.metaDescription ?? content.description,
            telephone: FIRM.phone,
            priceRange: `${FEES.sale}–${FEES.purchase}`,
            address: {
              "@type": "PostalAddress",
              streetAddress: office.street,
              addressLocality: office.suburb,
              addressRegion: office.state,
              postalCode: office.postcode,
              addressCountry: "AU",
            },
            areaServed: Array.from(
              new Set([content.name, ...content.nearby, ...(content.schemaAreas ?? [])]),
            ).map((n) => ({
              "@type": "Place",
              name: `${n}, Queensland`,
            })),
            serviceType: [
              "Residential conveyancing",
              "Contract review",
              "Seller disclosure statement (Form 2)",
              "PEXA electronic settlement",
            ],
            offers: [
              {
                "@type": "Offer",
                name: "Residential sale",
                price: FEES.sale.replace(/[$,]/g, ""),
                priceCurrency: "AUD",
              },
              {
                "@type": "Offer",
                name: "Residential purchase",
                price: FEES.purchase.replace(/[$,]/g, ""),
                priceCurrency: "AUD",
              },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              {
                "@type": "ListItem",
                position: 2,
                name: "Conveyancing",
                item: `${SITE}/conveyancing`,
              },
              { "@type": "ListItem", position: 3, name: content.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: ConveyancingLocationPage,
});

const GUIDE_LINKS = [
  {
    slug: "how-much-does-conveyancing-cost-in-queensland",
    t: "How much does conveyancing cost in Queensland?",
  },
  { slug: "cooling-off-period-queensland", t: "The Queensland cooling-off period" },
  {
    slug: "building-and-pest-report-came-back-bad",
    t: "The building and pest report came back bad — now what?",
  },
  {
    slug: "how-do-i-sell-my-property-privately-queensland",
    t: "How do I sell my property privately?",
  },
] as const;

const WHY_US = [
  {
    t: "A solicitor, not a processing centre",
    d: "Your file is run by an admitted Queensland solicitor who can give you legal advice on the contract, not a paralegal in a call centre who can only move paperwork.",
  },
  {
    t: "Local, not national",
    d: "We work from the peninsula and the Sunshine Coast. We know the councils, the overlays, the body corporates and the local agents — because we deal with them every week.",
  },
  {
    t: "Fixed fees, quoted in writing",
    d: "No 'from' pricing that grows at settlement. You get the professional fee in writing before we start, with searches and government fees itemised.",
  },
  {
    t: "Free contract review before you sign",
    d: "Send the contract through and we will read it and tell you what needs changing — at no cost and with no obligation to instruct us.",
  },
] as const;

function ConveyancingLocationPage() {
  const { content } = Route.useLoaderData() as { content: ConveyancingLocation };
  const office = officeForSlug(content.slug);
  const officeLine = `${office.street}, ${office.suburb} ${office.state} ${office.postcode}`;
  const solicitor = PEOPLE.find((p) => p.slug === (content.personSlug ?? "erin-wilkinson"));

  return (
    <>
      <AdLandingHero
        eyebrow={`Conveyancing solicitors — ${content.name}`}
        title={content.title}
        lead={content.lead}
        suburb={content.name}
        personSlug={content.personSlug ?? "erin-wilkinson"}
      />

      <ReviewCards />
      <WhatsIncluded />
      <SettlementSteps />
      {content.slug === "caloundra" && <LocalConveyancingDepth d={CALOUNDRA_DEPTH} />}

      {content.slug === "caloundra" && (
        <section className="mx-auto max-w-4xl px-5 pb-12">
          <h2 className="font-serif text-2xl text-foreground">Your local conveyancing solicitor in Caloundra</h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Erin Wilkinson acts for Caloundra buyers and sellers from our office at {officeLine}.
            Buying a Kings Beach unit, selling a Golden Beach home or coordinating a sale and
            purchase? Tell us the property address, whether you have signed and any contract
            deadlines so we can discuss the checks and quote relevant to your transaction.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Call <a href={`tel:${FIRM.phone.replace(/\s/g, "")}`} className="font-semibold text-primary underline">{FIRM.phone}</a>,
            use the quote form or <Link to="/contact" className="font-semibold text-primary underline">contact our Golden Beach office</Link>.
            Our professional conveyancing fees and additional charges are set out separately in your quote.
          </p>
          <h2 className="mt-8 font-serif text-2xl text-foreground">Looking for a Caloundra wills lawyer?</h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            A property purchase, a change in ownership or downsizing can be a useful time to
            review your will and enduring power of attorney. These are separate legal services,
            not included in your conveyancing fee. For appointments and estate-planning advice,
            see our <Link to="/wills/$suburb" params={{ suburb: "caloundra" }} className="font-semibold text-primary underline">Caloundra wills lawyers</Link>.
          </p>
        </section>
      )}

      {["redcliffe", "north-lakes"].includes(content.slug) && (
        <section className="mx-auto max-w-4xl px-5 pb-12">
          <h2 className="font-serif text-2xl text-foreground">Before you sign a {content.name} property contract</h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Send the contract and seller's Form 2 disclosure statement for a free pre-signing
            review. Tell us how you intend to use the property and whether you also need to sell.
            We can check the proposed conditions and explain the professional fee and additional
            charges before you decide to instruct us. Already signed? Include your contract date
            and any finance, building and pest or settlement deadlines.
          </p>
          <ul className="mt-4 space-y-3 text-base">
            <li><Link to="/form-2-buyer-rights-qld" className="font-semibold text-primary underline">Form 2 buyer rights: disclosure is not the whole due diligence process</Link></li>
            <li><Link to="/buyers-solicitor-qld" className="font-semibold text-primary underline">Free pre-signing contract review and buyer checks</Link></li>
            <li><Link to="/conveyancing-cost-qld" className="font-semibold text-primary underline">Conveyancing costs: what to include in your budget</Link></li>
          </ul>
        </section>
      )}

      {["redcliffe", "hervey-bay"].includes(content.slug) && (
        <section className="mx-auto max-w-4xl px-5 pb-12">
          <h2 className="font-serif text-2xl text-foreground">Conveyancing in {content.name}: fees and how we act</h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">{content.answer}</p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Compare the professional fee and third-party charges in our{" "}
            <Link to="/conveyancing-cost-qld" className="font-semibold text-primary underline">Queensland conveyancing cost guide</Link>{" "}
            and <Link to="/fees" className="font-semibold text-primary underline">fee schedule</Link>.
            {content.slug === "redcliffe" ? (
              <> For an appointment, see our <Link to="/scarborough-office" className="font-semibold text-primary underline">Scarborough office</Link>. Before buying an apartment, read <Link to="/guides/$slug" params={{ slug: "buying-a-unit-redcliffe-peninsula" }} className="font-semibold text-primary underline">our Redcliffe Peninsula unit checklist</Link>.</>
            ) : (
              <> We act remotely from Golden Beach and Scarborough, not a Hervey Bay office. See <Link to="/pexa-settlement" className="font-semibold text-primary underline">how electronic settlement works</Link>.</>
            )}
          </p>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-5 pb-12">
        <h2 className="font-serif text-3xl text-foreground">
          What we check on a {content.name} contract
        </h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {content.checks.slice(0, 2).map((c) => (
            <div key={c.t} className="rounded-md border border-border bg-card p-5">
              <h3 className="font-serif text-lg text-foreground">{c.t}</h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <TrustAndOffices />

      <section className="mx-auto max-w-6xl px-5 pb-12">
        <h2 className="font-serif text-3xl text-foreground">{content.name} conveyancing questions</h2>
        <div className="mt-6 max-w-3xl">
          <FaqList items={content.faqs.slice(0, 4)} />
        </div>
      </section>

      <section className="mx-auto max-w-xl px-5 pb-12">
        <QuickQuoteForm id="enquiry-bottom" suburb={content.name} heading={`Ready? Get your fixed ${content.name} quote`} />
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-24">
        <details className="rounded-md border border-border bg-card p-6">
          <summary className="cursor-pointer font-serif text-xl text-foreground">
            More about conveyancing in {content.answerLabel ?? content.name}
          </summary>
          {content.editorialByline && (
            <p className="mt-4 text-sm text-muted-foreground">
              By {content.editorialByline} · Coastside Law
            </p>
          )}
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">{content.answer}</p>
          {!content.editorialByline && <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Also searched as: {content.variants.join(", ")}.
          </p>}
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {WHY_US.map((w) => (
              <div key={w.t} className="rounded-sm border border-border p-5">
                <h3 className="font-serif text-lg text-foreground">{w.t}</h3>
                <p className="mt-2 text-base leading-relaxed text-muted-foreground">{w.d}</p>
              </div>
            ))}
          </div>
          {content.paragraphs.map((p) => (
            <p key={p} className="mt-5 text-base leading-relaxed text-muted-foreground">{p}</p>
          ))}
          {content.checks.slice(2).map((c) => (
            <div key={c.t} className="mt-5">
              <h3 className="font-serif text-lg text-foreground">{c.t}</h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
          {content.longTail?.map((b) => (
            <div key={b.h}>
              <h2 className="mt-8 font-serif text-2xl text-foreground">{b.h}</h2>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">{b.p}</p>
            </div>
          ))}
          {content.sources && (
            <div className="mt-8">
              <h2 className="font-serif text-2xl text-foreground">Council information for your property</h2>
              <ul className="mt-4 space-y-2">
                {content.sources.map((source) => <li key={source.href}><a href={source.href} target="_blank" rel="noopener noreferrer" className="text-primary underline">{source.label}</a></li>)}
              </ul>
            </div>
          )}
          {content.faqs.length > 4 && (
            <div className="mt-8">
              <FaqList items={content.faqs.slice(4)} />
            </div>
          )}
          <p className="mt-8 text-base leading-relaxed text-muted-foreground">
            We act on property across {content.name} and {content.nearby.join(", ")} from {officeLine}.
            {solicitor ? ` ${solicitor.name} will usually run your file.` : ""}
          </p>
          <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who will act for you" />
          <h2 className="mt-10 font-serif text-2xl text-foreground">Related pages and guides</h2>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/conveyancing-cost-qld" className="font-semibold text-primary underline">Conveyancing costs: professional fees and third-party charges</Link></li>
            <li><Link to="/buyers-solicitor-qld" className="font-semibold text-primary underline">Buyer contract review and practical checks</Link></li>
            <li><Link to="/conveyancing-sunshine-coast" className="font-semibold text-primary underline">Sunshine Coast conveyancing</Link></li>
            <li><Link to="/fees" className="font-semibold text-primary underline">Full fee schedule</Link></li>
            <li><Link to="/seller-disclosure" className="font-semibold text-primary underline">Seller disclosure and the Form 2</Link></li>
            <li><Link to="/stamp-duty-calculator-qld" className="font-semibold text-primary underline">Queensland stamp duty calculator</Link></li>
            <li><Link to="/pexa-settlement" className="font-semibold text-primary underline">How PEXA settlement works</Link></li>
            {GUIDE_LINKS.map((g) => (
              <li key={g.slug}>
                <Link to="/guides/$slug" params={{ slug: g.slug }} className="font-semibold text-primary underline">{g.t}</Link>
              </li>
            ))}
          </ul>
          <h2 className="mt-10 font-serif text-2xl text-foreground">Other areas we act in</h2>
          <ul className="mt-4 flex flex-wrap gap-2">
            {CONVEYANCING_LOCATIONS.filter((l) => l.slug !== content.slug).map((l) => (
              <li key={l.slug}>
                <Link to="/conveyancing/$suburb" params={{ suburb: l.slug }} className="inline-block rounded-sm border border-border px-3 py-1.5 text-sm text-foreground hover:border-primary">
                  Conveyancing {l.name}
                </Link>
              </li>
            ))}
          </ul>
        </details>
      </section>
    </>
  );
}
